import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, EmptyState, Select } from '@adop/ui';
import { BarChart3 } from 'lucide-react';
import { formatCents } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import { listOrderItemsByLocation, listOrders } from '../../lib/data';
import { computeDailyStats, computeTopItems } from '../../lib/stats';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

function shortDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${Number(month)}/${Number(day)}`;
}

export function StatsPage() {
  const { location } = useWorkspace();
  const [days, setDays] = useState(7);
  const state = useAsync(async () => {
    if (!location) return { orders: [], items: [] };
    const [orders, items] = await Promise.all([
      listOrders(location.id, 2000),
      listOrderItemsByLocation(location.id),
    ]);
    return { orders, items };
  }, [location?.id]);

  const chartData = useMemo(() => {
    if (!location || !state.data) return [];
    return computeDailyStats(state.data.orders, location.timezone, days).map((stat) => ({
      ...stat,
      label: shortDate(stat.date),
      revenue: stat.revenueCents / 100,
    }));
  }, [state.data, location, days]);

  const topItems = useMemo(() => {
    if (!location || !state.data) return [];
    return computeTopItems(state.data.items, state.data.orders, location.timezone, days);
  }, [state.data, location, days]);

  if (!location) {
    return <EmptyState icon={<BarChart3 className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Stats"
        description="Orders, revenue and best sellers for this location."
        action={
          <div className="w-36">
            <Select
              value={String(days)}
              onChange={(event) => setDays(Number(event.target.value))}
              aria-label="Date range"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </Select>
          </div>
        }
      />

      {state.error ? <Alert>{state.error}</Alert> : null}

      {state.loading ? (
        <LoadingBlock />
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Orders per day</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#18181b" radius={[4, 4, 0, 0]} name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Revenue per day</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip
                      formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Top items</CardTitle>
            </CardHeader>
            <CardContent>
              {topItems.length === 0 ? (
                <p className="text-sm text-zinc-500">No orders in this period yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
                        <th className="py-2">Item</th>
                        <th className="py-2 text-right">Quantity</th>
                        <th className="py-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topItems.map((item) => (
                        <tr key={item.name} className="border-b border-zinc-100 last:border-0">
                          <td className="py-2 text-zinc-900">{item.name}</td>
                          <td className="py-2 text-right text-zinc-600">{item.quantity}</td>
                          <td className="py-2 text-right text-zinc-600">{formatCents(item.revenueCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
